import { useState, useEffect } from 'react'
import { useStackApp } from '@stackframe/stack'
import gardenopsLogo from '../assets/gardenops-logo.png'
import { HexagonIcon, BoardIcon, LightningIcon, LockIcon } from './icons/Icons'

function MicrosoftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

function FingerprintIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/>
      <path d="M14 13.12c0 2.38 0 6.38-1 8.88"/>
      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/>
      <path d="M2 12a10 10 0 0 1 18-6"/>
      <path d="M2 17.5c3.8-2.9 5.5-8 5.5-8"/>
      <path d="M20 12c0 2-.33 4-.95 5.5"/>
      <path d="M6.71 6.31A10 10 0 0 0 2 12"/>
    </svg>
  )
}

function SignInCard() {
  const stackApp = useStackApp()
  const [mode, setMode]           = useState('options') // 'options' | 'email'
  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)
  const [passkeySupported, setPasskeySupported] = useState(false)

  useEffect(() => {
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPasskeySupported)
        .catch(() => setPasskeySupported(false))
    }
  }, [])

  const handleMicrosoft = () => {
    setError(null)
    stackApp.signInWithOAuth('microsoft')
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    if (!email || !password) return
    setLoading(true)
    setError(null)
    const result = await stackApp.signInWithCredential({ email, password })
    if (result?.status === 'error') {
      const msg = result.error?.message ?? result.error?.toString() ?? null
      setError(msg || 'Incorrect email or password.')
    }
    setLoading(false)
  }

  const handlePasskey = async () => {
    setLoading(true)
    setError(null)
    const result = await stackApp.signInWithPasskey()
    if (result?.status === 'error') {
      const msg = result.error?.message ?? result.error?.toString() ?? null
      setError(msg || 'Biometric sign-in failed or was cancelled.')
    }
    setLoading(false)
  }

  return (
    <div className="lp-signin-card" role="region" aria-label="Sign in">

      {/* Microsoft OAuth — always visible */}
      <button
        className="lp-signin-oauth"
        onClick={handleMicrosoft}
        disabled={loading}
        aria-label="Sign in with Microsoft"
      >
        <MicrosoftIcon />
        Sign in with Microsoft
      </button>

      <div className="lp-signin-divider" aria-hidden="true">
        <span className="lp-signin-divider-line" />
        <span className="lp-signin-divider-text">OR</span>
        <span className="lp-signin-divider-line" />
      </div>

      {/* Email / Password */}
      {mode === 'options' ? (
        <button
          className="lp-signin-method-btn"
          onClick={() => setMode('email')}
          disabled={loading}
        >
          Email &amp; Password
        </button>
      ) : (
        <form className="lp-signin-form" onSubmit={handleEmailSubmit} noValidate>
          <input
            className="lp-signin-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
            disabled={loading}
            aria-label="Email address"
          />
          <input
            className="lp-signin-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={loading}
            aria-label="Password"
          />
          <div className="lp-signin-form-row">
            <button
              type="button"
              className="lp-signin-back"
              onClick={() => { setMode('options'); setError(null) }}
              disabled={loading}
            >
              ← Back
            </button>
            <button
              type="submit"
              className="lp-signin-submit"
              disabled={loading || !email || !password}
            >
              {loading ? 'SIGNING IN…' : 'SIGN IN'}
            </button>
          </div>
        </form>
      )}

      {/* Biometric / Passkey */}
      {passkeySupported && (
        <button
          className="lp-signin-method-btn lp-signin-method-btn--bio"
          onClick={handlePasskey}
          disabled={loading}
          aria-label="Sign in with biometrics or passkey"
        >
          <FingerprintIcon />
          Biometric / Passkey
        </button>
      )}

      {/* Error */}
      {error && (
        <p className="lp-signin-error" role="alert">{error}</p>
      )}
    </div>
  )
}

const FEATURES = [
  {
    glyph: <HexagonIcon width="24" height="24" />,
    label: 'Isometric Facility Map',
    desc: 'Real-time visual overview of every cultivation room — overlays, modes, and status at a glance.',
  },
  {
    glyph: <BoardIcon width="24" height="24" />,
    label: 'Compliance Logging',
    desc: 'Spray logs, pot checks, filter changes, and net tracking with full timestamped audit trails.',
  },
  {
    glyph: <LightningIcon width="24" height="24" />,
    label: 'Live Sync',
    desc: 'WebSocket-powered updates across all team members — every change appears instantly facility-wide.',
  },
  {
    glyph: <LockIcon width="24" height="24" />,
    label: 'Role-Based Access',
    desc: 'Grower, Master Grower, and Director permission tiers enforced end-to-end.',
  },
]

export default function LandingPage() {
  return (
    <div className="lp-shell" role="main">

      {/* ── Nav ─────────────────────────────────────────────── */}
      <nav className="lp-nav" aria-label="Main navigation">
        <div className="lp-nav-logo">
          <img src={gardenopsLogo} alt="" aria-hidden="true" className="lp-nav-logo-img" />
          <span className="lp-nav-wordmark">GARDENOPS</span>
          <span className="lp-nav-divider" aria-hidden="true" />
          <span className="lp-nav-sub">MISSION CONTROL</span>
        </div>
        <div className="lp-nav-actions">
          <a href="#signin" className="lp-btn lp-btn--ghost">Sign In</a>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="lp-hero" aria-labelledby="hero-headline">
        <div className="lp-hero-eyebrow">MTL CANNABIS — CULTIVATION PLATFORM</div>
        <h1 id="hero-headline" className="lp-hero-headline">
          MISSION<br />CONTROL
        </h1>
        <p className="lp-hero-desc">
          A centralized command center for your cultivation facility.
          Monitor every room, log compliance events, and coordinate
          your team — all in real time.
        </p>
        <div className="lp-hero-live" aria-label="Platform status: live">
          <span className="lp-live-dot" aria-hidden="true" />
          SYSTEM LIVE
        </div>
      </section>

      {/* ── Sign In ──────────────────────────────────────────── */}
      <section className="lp-signin-section" id="signin" aria-labelledby="signin-heading">
        <h2 id="signin-heading" className="lp-signin-heading">SIGN IN</h2>
        <p className="lp-signin-sub">Access is managed by your organization administrator.</p>
        <SignInCard />
      </section>

      {/* ── Platform Overview ───────────────────────────────── */}
      <section className="lp-overview" aria-labelledby="overview-heading">
        <div className="lp-overview-inner">
          <h2 id="overview-heading" className="lp-section-title">WHAT IS MISSION CONTROL?</h2>
          <p className="lp-overview-body">
            Mission Control is the operational backbone of the MTL Cannabis cultivation facility.
            From an isometric real-time map of all grow rooms to detailed compliance logs and
            instant team messaging — it gives growers and directors a unified view of the
            entire operation.
          </p>

          <ul className="lp-features" role="list">
            {FEATURES.map(f => (
              <li key={f.label} className="lp-feature-card">
                <span className="lp-feature-glyph" aria-hidden="true">{f.glyph}</span>
                <div>
                  <div className="lp-feature-label">{f.label}</div>
                  <div className="lp-feature-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Secondary CTA ───────────────────────────────────── */}
      <section className="lp-cta-section" aria-label="Enter the platform">
        <h2 className="lp-cta-heading">READY TO ENTER?</h2>
        <p className="lp-cta-sub">Sign in above with Microsoft, email/password, or biometric to access the dashboard.</p>
        <a href="#signin" className="lp-btn lp-btn--cta">
          Sign In
        </a>
      </section>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="lp-footer" role="contentinfo">
        <img src={gardenopsLogo} alt="" aria-hidden="true" className="lp-footer-logo-img" />
        <span>GARDENOPS — MTL CANNABIS</span>
        <span className="lp-footer-sep" aria-hidden="true">·</span>
        <span>Confidential — Authorized Access Only</span>
      </footer>

    </div>
  )
}
